export interface GoogleTagManagerConfig {
  enabled: boolean
  containerIds: string[]
}

declare global {
  interface Window {
    dataLayer?: unknown[]
  }
}

const CONTAINER_ID_PATTERN = /^GTM-[A-Z0-9]+$/i
const GTM_SCRIPT_ID_PREFIX = 'cdt-gtm-script-'

export function normalizeContainerIds(ids?: string[]): string[] {
  if (!Array.isArray(ids)) return []
  const seen = new Set<string>()
  const result: string[] = []
  for (const raw of ids) {
    const id = String(raw ?? '').trim().toUpperCase()
    if (id && CONTAINER_ID_PATTERN.test(id) && !seen.has(id)) {
      seen.add(id)
      result.push(id)
    }
  }
  return result
}

export function parseContainerIdsText(text: string): string[] {
  return normalizeContainerIds(text.split(/[\n,]+/))
}

export function formatContainerIdsText(ids: string[]): string {
  return normalizeContainerIds(ids).join('\n')
}

export function parseGoogleTagManagerConfig(raw?: string | null): GoogleTagManagerConfig {
  if (!raw?.trim()) {
    return { enabled: false, containerIds: [] }
  }

  try {
    const parsed = JSON.parse(raw) as Partial<GoogleTagManagerConfig>
    return {
      enabled: parsed.enabled === true,
      containerIds: normalizeContainerIds(parsed.containerIds),
    }
  } catch {
    return { enabled: false, containerIds: [] }
  }
}

let initialized = false
let consentDefaultsSet = false

function ensureDataLayer(): void {
  window.dataLayer = window.dataLayer || []
}

/**
 * Pushes an argument list in the exact shape produced by the standard gtag
 * stub (`function gtag(){ dataLayer.push(arguments) }`), which Consent Mode
 * relies on.
 */
function gtag(...args: unknown[]): void {
  window.dataLayer?.push(args)
}

interface GtmConsentState {
  analytics: boolean
  marketing: boolean
}

function consentPayload(state: GtmConsentState): Record<string, 'granted' | 'denied'> {
  return {
    analytics_storage: state.analytics ? 'granted' : 'denied',
    ad_storage: state.marketing ? 'granted' : 'denied',
    ad_user_data: state.marketing ? 'granted' : 'denied',
    ad_personalization: state.marketing ? 'granted' : 'denied',
  }
}

/**
 * Consent Mode v2 defaults. Must run before the GTM container loads so tags
 * that respect consent are held back until the user decides.
 */
function setDefaultConsent(): void {
  if (consentDefaultsSet) return
  gtag('consent', 'default', {
    ...consentPayload({ analytics: false, marketing: false }),
    wait_for_update: 500,
  })
  consentDefaultsSet = true
}

function loadContainer(containerId: string): void {
  const scriptId = `${GTM_SCRIPT_ID_PREFIX}${containerId}`
  if (document.getElementById(scriptId)) return

  const script = document.createElement('script')
  script.id = scriptId
  script.async = true
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`
  document.head.appendChild(script)
}

export function isGoogleTagManagerInitialized(): boolean {
  return initialized
}

export function initGoogleTagManager(containerIds: string[]): boolean {
  if (!import.meta.client) return false

  const ids = normalizeContainerIds(containerIds)
  if (!ids.length) return false

  ensureDataLayer()
  setDefaultConsent()

  gtag('js', new Date())
  window.dataLayer?.push({ 'gtm.start': Date.now(), event: 'gtm.js' })

  for (const id of ids) {
    loadContainer(id)
  }

  initialized = true
  return true
}

export function updateGoogleTagManagerConsent(state: GtmConsentState): void {
  if (!import.meta.client) return
  ensureDataLayer()
  gtag('consent', 'update', consentPayload(state))
}

export function trackGtmPageView(path: string): void {
  if (!import.meta.client || !initialized) return
  window.dataLayer?.push({
    event: 'page_view',
    page_path: path,
    page_location: window.location.href,
  })
}
